import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { CreateUpdateBlockedUserForm } from '../CreateUpdateBlockedUserForm'
import { societyBlockCreateSchema } from '../../../../forms/schemas/society_block_create'
import { addBlockedUsers } from '../../../../api/societies'
import {
  chatMemberCandidatesQueryKey,
  chatRecipientsQueryKey,
} from '../../../../api/chat'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'

interface CreateUpdateBlockedUserModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  society: ISocietiesModel
}

export const CreateUpdateBlockedUserModal = ({
  children,
  show,
  close,
  society,
}: CreateUpdateBlockedUserModalProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const queryClient = useQueryClient()

  const { mutate: mutateAddBlockedUsers, isPending: isUpdatingBlockedUsers } =
    useMutation({
      mutationFn: async (params: { societyId: string; userIds: string[] }) => {
        return (await addBlockedUsers(params.societyId, params.userIds)).data
          .data
      },

      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: chatRecipientsQueryKey })
        queryClient.invalidateQueries({
          queryKey: chatMemberCandidatesQueryKey,
        })
        setToastNotification(
          ToastType.SUCCESS,
          translate('societyBlockedUsersView.flashMessage.userBlockAddSuccess')
        )
        close()
      },
      onError: (error) => {
        setToastNotification(
          ToastType.DANGER,
          translate('societyBlockedUsersView.flashMessage.userBlockAddFailure')
        )

        captureException(error)
      },
    })
  const onSubmit = async (
    data: InferType<typeof societyBlockCreateSchema>
  ): Promise<void> => {
    const selectedUserIds = data.userIds
      .map((_userId) => _userId)
      .filter((_userId) => _userId !== undefined) as string[]

    mutateAddBlockedUsers({ userIds: selectedUserIds, societyId: society?._id })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isUpdatingBlockedUsers

  return (
    <>
      {children}
      <Modal show={show} size="md" minHeight={400}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate('createUpdateBlockedUserModal.title')}
            </ModalHeader>
          ),
          body: (
            <ModalBody className="justify-between">
              <CreateUpdateBlockedUserForm
                society={society}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
